import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { FirebaseApp } from 'firebase/app';

// VAPID key for Firebase web push - Replace with your actual VAPID key
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || 'your-vapid-key-here';

export interface FCMTokenData {
  token: string;
  deviceInfo: DeviceInfo;
}

export interface DeviceInfo {
  deviceId: string;
  deviceType: string;
  deviceModel: string;
  osVersion: string;
  appVersion: string;
  browserName: string;
  browserVersion: string;
}

class FCMService {
  private messaging: Messaging | null = null;

  initialize(app: FirebaseApp) {
    this.messaging = getMessaging(app);
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications');
    }

    if (Notification.permission === 'granted') {
      return Notification.permission;
    }

    const permission = await Notification.requestPermission();
    return permission;
  }  async getFCMToken(): Promise<string | null> {
    if (!this.messaging) {
      console.error('FCM Service: messaging not initialized');
      throw new Error('FCM not initialized');
    }

    if (!VAPID_KEY || VAPID_KEY === 'your-vapid-key-here') {
      console.error('FCM Service: VAPID key is not configured properly');
      throw new Error('VAPID key is not configured. Please set REACT_APP_FIREBASE_VAPID_KEY in your environment file.');
    }

    try {
      console.log('FCM Service: Requesting token with VAPID key configured');
      const currentToken = await getToken(this.messaging, {
        vapidKey: VAPID_KEY,
      });

      if (currentToken) {
        console.log('FCM Service: Token obtained successfully:', currentToken.substring(0, 20) + '...');
        return currentToken;
      } else {
        console.log('FCM Service: No registration token available. This could be due to:');
        console.log('1. User has not granted notification permission');
        console.log('2. Service worker is not registered properly');
        console.log('3. Browser does not support push messaging');
        return null;
      }
    } catch (err) {
      console.error('FCM Service: Error occurred while retrieving token:', err);
      throw err;
    }
  }

  setupMessageListener(callback: (payload: any) => void) {
    if (!this.messaging) {
      throw new Error('FCM not initialized');
    }

    return onMessage(this.messaging, (payload) => {
      console.log('Message received in foreground:', payload);
      callback(payload);
    });
  }

  generateDeviceId(): string {
    // Generate a unique device ID based on browser fingerprint
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx!.textBaseline = 'top';
    ctx!.font = '14px Arial';
    ctx!.fillText('Device fingerprint', 2, 2);
    
    const fingerprint = canvas.toDataURL();
    const deviceId = btoa(fingerprint).substring(0, 32);
    
    // Store in localStorage for consistency
    const storedDeviceId = localStorage.getItem('deviceId');
    if (storedDeviceId) {
      return storedDeviceId;
    }
    
    localStorage.setItem('deviceId', deviceId);
    return deviceId;
  }

  getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const deviceId = this.generateDeviceId();
    
    // Parse browser information
    let browserName = 'Unknown';
    let browserVersion = 'Unknown';
    
    if (userAgent.includes('Chrome')) {
      browserName = 'Chrome';
      const match = userAgent.match(/Chrome\/([0-9.]+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox';
      const match = userAgent.match(/Firefox\/([0-9.]+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Safari')) {
      browserName = 'Safari';
      const match = userAgent.match(/Version\/([0-9.]+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Edge')) {
      browserName = 'Edge';
      const match = userAgent.match(/Edge\/([0-9.]+)/);
      browserVersion = match ? match[1] : 'Unknown';
    }

    // Detect device type
    let deviceType = 'desktop';
    if (/Mobi|Android/i.test(userAgent)) {
      deviceType = 'mobile';
    } else if (/Tablet|iPad/i.test(userAgent)) {
      deviceType = 'tablet';
    }

    // Detect OS
    let osVersion = 'Unknown';
    if (userAgent.includes('Windows')) {
      osVersion = 'Windows';
      const match = userAgent.match(/Windows NT ([0-9.]+)/);
      if (match) osVersion += ` ${match[1]}`;
    } else if (userAgent.includes('Mac OS')) {
      osVersion = 'macOS';
      const match = userAgent.match(/Mac OS X ([0-9_]+)/);
      if (match) osVersion += ` ${match[1].replace(/_/g, '.')}`;
    } else if (userAgent.includes('Linux')) {
      osVersion = 'Linux';
    } else if (userAgent.includes('Android')) {
      osVersion = 'Android';
      const match = userAgent.match(/Android ([0-9.]+)/);
      if (match) osVersion += ` ${match[1]}`;
    } else if (userAgent.includes('iOS')) {
      osVersion = 'iOS';
      const match = userAgent.match(/OS ([0-9_]+)/);
      if (match) osVersion += ` ${match[1].replace(/_/g, '.')}`;
    }

    return {
      deviceId,
      deviceType,
      deviceModel: navigator.platform || 'Unknown',
      osVersion,
      appVersion: process.env.REACT_APP_VERSION || '1.0.0',
      browserName,
      browserVersion,
    };
  }

  async getTokenWithDeviceInfo(): Promise<FCMTokenData | null> {
    const token = await this.getFCMToken();
    if (!token) return null;

    return {
      token,
      deviceInfo: this.getDeviceInfo(),
    };
  }
}

export const fcmService = new FCMService();
export default fcmService;
